/**
 * @fileoverview Chat thread component for displaying message history
 * @module ctl-chat-thread
 * @version 1.0.0
 * 
 * @description
 * A scrollable container for displaying a thread of chat messages.
 * Automatically scrolls to the bottom when new messages are added.
 * 
 * @example
 * <ctl-chat-thread>
 *   <ctl-chat-message sender="assistant" timestamp="2:30 PM">
 *     Hello! How can I help you?
 *   </ctl-chat-message>
 *   <ctl-chat-message sender="user" timestamp="2:31 PM">
 *     I have a question about your services.
 *   </ctl-chat-message>
 * </ctl-chat-thread>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-chat-thread-bg: var(--ctl-color-surface-secondary, #f9f9f9);
    --ctl-chat-thread-border: var(--ctl-color-border, #ddd);
    --ctl-chat-thread-padding: 1rem;
    --ctl-chat-thread-height: 400px;
    --ctl-chat-thread-radius: var(--ctl-radius-md, 8px);

    display: block;
    height: var(--ctl-chat-thread-height);
    overflow-y: auto;
    overflow-x: hidden;
    padding: var(--ctl-chat-thread-padding);
    background: var(--ctl-chat-thread-bg);
    border: 1px solid var(--ctl-chat-thread-border);
    border-radius: var(--ctl-chat-thread-radius);
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    scroll-behavior: smooth;
  }

  /* Custom scrollbar styling */
  :host::-webkit-scrollbar {
    width: 8px;
  }

  :host::-webkit-scrollbar-track {
    background: transparent;
  }

  :host::-webkit-scrollbar-thumb {
    background: var(--ctl-color-border, #ddd);
    border-radius: 4px;
  }

  :host::-webkit-scrollbar-thumb:hover {
    background: var(--ctl-color-muted, #999);
  }

  .container {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  ::slotted(*) {
    flex-shrink: 0;
  }
</style>

<div class="container" part="container">
  <slot></slot>
</div>
`;

/**
 * Chat Thread Web Component
 * 
 * @class CtlChatThread
 * @extends HTMLElement
 * 
 * @attr {string} height - Custom height for the thread container (default: 400px)
 * @attr {boolean} auto-scroll - Automatically scroll to bottom on content changes (default: true)
 * 
 * @slot - Default slot for chat messages
 * 
 * @csspart container - The inner container element
 * 
 * @cssprop --ctl-chat-thread-bg - Background color (default: #f9f9f9)
 * @cssprop --ctl-chat-thread-border - Border color (default: #ddd)
 * @cssprop --ctl-chat-thread-padding - Internal padding (default: 1rem)
 * @cssprop --ctl-chat-thread-height - Container height (default: 400px)
 * @cssprop --ctl-chat-thread-radius - Border radius (default: 8px)
 */
export class CtlChatThread extends HTMLElement {
  static get observedAttributes() {
    return ["height", "auto-scroll"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._container = this.shadowRoot.querySelector(".container");
    this._observer = null;
  }

  connectedCallback() {
    // Default auto-scroll to true
    if (!this.hasAttribute("auto-scroll")) {
      this.setAttribute("auto-scroll", "");
    }

    this._syncAttributes();
    this._setupAutoScroll();
  }

  disconnectedCallback() {
    this._teardownAutoScroll();
  }

  attributeChangedCallback(name) {
    if (name === "height") {
      this._syncAttributes();
    } else if (name === "auto-scroll") {
      if (this.hasAttribute("auto-scroll")) {
        this._setupAutoScroll();
      } else {
        this._teardownAutoScroll();
      }
    }
  }

  _syncAttributes() {
    const height = this.getAttribute("height");
    if (height) {
      this.style.setProperty("--ctl-chat-thread-height", height);
    }
  }

  _setupAutoScroll() {
    if (this._observer) return;

    // Use MutationObserver to detect when content changes
    this._observer = new MutationObserver(() => {
      if (this.hasAttribute("auto-scroll")) {
        this.scrollToBottom();
      }
    });

    this._observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  _teardownAutoScroll() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // Public API
  scrollToBottom(smooth = true) {
    if (smooth) {
      this.scrollTo({
        top: this.scrollHeight,
        behavior: "smooth"
      });
    } else {
      this.scrollTop = this.scrollHeight;
    }
  }

  scrollToTop(smooth = true) {
    if (smooth) {
      this.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    } else {
      this.scrollTop = 0;
    }
  }

  clear() {
    // Remove all child nodes
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
  }
}

if (!customElements.get("ctl-chat-thread")) {
  customElements.define("ctl-chat-thread", CtlChatThread);
}

export default CtlChatThread;
